import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import PatronNavbar from "../Patron_Navbar";
import "./CloseUploadedOpportunities.css";
import { makeAuthenticatedPOSTRequest, makeAuthenticatedGETRequest } from "../../services/serverHelper";
import { patronProfilePoints } from "../../services/apis";
import {
  AiOutlineSave,
  AiOutlineFileText,
  AiOutlineCalendar,
  AiOutlineUser,
  AiOutlineDelete,
  AiOutlineCloudUpload,
  AiOutlineAlert,
} from "react-icons/ai";
import { MdLocationOn, MdTheaters, MdClose } from "react-icons/md";
import PatronSidebar from "../PatronSidebar";
import StarReview from "../../../components/StarReview";

const CloseUploadedOpportunities = () => {
  const { accessToken } = useSelector((state) => state.auth);
  const [opportunityData, setOpportunityData] = useState(null);
  const [hiredArtists, setHiredArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { opportunity } = state || {};
  const fileInputRef = useRef(null);
  const {id} = useParams()

  // Form State
  const [performanceDetails, setPerformanceDetails] = useState({
    completionDate: "",
    summary: "",
    remarks: "",
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const [locationDenied, setLocationDenied] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [reviews, setReviews] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch opportunity details and hired artists on component mount
  useEffect(() => {
      fetchOpportunityDetails();
  }, []);

  const fetchOpportunityDetails = async () => {
      setLoading(true);

      // Fetch hired artists for this opportunity
      const artistsResponse = await makeAuthenticatedGETRequest(
        `${patronProfilePoints.FETCH_PATRON_OPPOR_API}/${id}`,
        accessToken
      );

      console.log(artistsResponse.data)

      if (artistsResponse.statusCode === 200) {
        setLoading(false);
        setOpportunityData(artistsResponse.data);

        const artists = await Promise.all(
          artistsResponse.data.hiredArtists.map(async (data) => {
              const artistData = await makeAuthenticatedGETRequest(
                  `${patronProfilePoints.FETCH_PATRON_ALL_ARTIST_API}/${data.appliedBy}`,
                  accessToken
              );

              // console.log(artistData.data);

              return artistData.data;
          })
        );
        setHiredArtists(artists);

        // Initialize reviews object with empty reviews for each artist
        const initialReviews = {};

        hiredArtists.forEach((app) => {
          initialReviews[app.artistId] = {
            rating: 0,
            comment: "",
            applicationId:opportunityData.hiredArtists.find((a) => a.appliedBy === app.artistId)._id || "",
          };
        });
        setReviews(initialReviews);
      

    //   setOpportunityData(opportunity);
    } 
  };

  // Handle performance details change
  const handlePerformanceDetailsChange = (e) => {
    const { name, value } = e.target;
    setPerformanceDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Get current geolocation
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setLocationDenied(true);
        reject(new Error("Geolocation not supported"));
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            console.error("Geolocation error:", error);
            setLocationDenied(true);
            reject(error);
          }
        );
      }
    });
  };

  const uploadPerformanceImages = async (formData) => {
    const response = await axios.post(
      patronProfilePoints.UPLOAD_PERFORMANCE_IMAGES_API,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );

    return response.data;
};

  // Handle image upload
  const handleImageUpload = async (e) => {
  const files = Array.from(e.target.files);

  if (files.length === 0) return;

  try {
    setUploading(true);

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file); // must match multer field name
    });

    const response = await uploadPerformanceImages(formData);
    console.log(response.data);

    setUploadedImages((prev) => [
      ...prev,
      ...(response.data || [])
    ]);

    fileInputRef.current.value = "";
  } catch (error) {
    console.error(error);
    toast.error("Failed to upload images");
  } finally {
    setUploading(false);
  }
};

  // Remove uploaded image
  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle review change
  const handleReviewChange = (artistId, field, value) => {
    console.log("Current reviews state before update:", reviews);
    setReviews((prev) => ({
      ...prev,
      [artistId]: {
        ...prev[artistId],
        [field]: value,
      },
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!performanceDetails.completionDate) {
      errors.completionDate = "Event completion date is required";
    }

    if (!performanceDetails.summary || performanceDetails.summary.trim() === "") {
      errors.summary = "Performance summary is required";
    }

    // if (uploadedImages.length === 0) {
    //   errors.images = "At least one performance proof image is required";
    // }

    // Check all artists have ratings
    for (const app of hiredArtists) {
      const artistId = app.artistId;
      if (!reviews[artistId] || reviews[artistId].rating === 0) {
        errors[`rating_${artistId}`] = "Rating required for all artists";
      }
    }

    console.log("Validation Errors:", errors);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Submitting performance details...");

    try {
      // Prepare the final payload
      const payload = {
        opportunityId: opportunityData._id,
        performanceDate: performanceDetails.completionDate,
        performanceSummary: performanceDetails.summary,
        notes: performanceDetails.remarks,
        imageUploadLink: uploadedImages,
        // imageUploadLink: uploadedImages, // Send the file objects directly
        reviews: Object.entries(reviews).map(([artistId, review]) => ({
          artistId,
          applicationId: opportunityData.hiredArtists.find((a) => a.appliedBy === artistId)._id || "",
          rating: review.rating,
          comment: review.comment,
        })),
      };

      console.log("Submitting payload:", payload);

      // Make the API request
      const response = await makeAuthenticatedPOSTRequest(
        `${patronProfilePoints.CREATE_PERFORMANCE_API}`,
        payload,
        accessToken
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success("Opportunity closed successfully!");
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        toast.error(response.message || "Failed to close opportunity");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
      toast.dismiss(toastId);
    }
  };

  if (loading) {
    return (
        <>
        <PatronNavbar />
            <div className="close_oppor_page">
                <div className="close_oppor_loading">
                <div className="close_oppor_spinner"></div>
                <p>Loading opportunity details...</p>
            </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PatronNavbar />
      <div className="dashboard-layout">
        <PatronSidebar />
        <div className="dashboard-content">
            {/* Hero Section */}
            <div className="close_oppor_hero">
            <div className="close_oppor_hero_overlay">
                <h1 className="close_oppor_hero_title">
                <MdTheaters style={{ marginRight: "0.5rem" }} />
                Close Opportunity
                </h1>
                <p className="close_oppor_hero_subtitle">
                Complete performance details and provide artist reviews
                </p>
            </div>
            </div>

            <div className="close_oppor_container">
            {/* Opportunity Info Card */}
            {opportunityData && (
                <div className="close_oppor_info_card">
                <div className="close_oppor_info_header">
                    <h3 className="close_oppor_info_title">{opportunityData.artName}</h3>
                    <p className="close_oppor_info_subtitle">{opportunityData.description}</p>
                </div>
                <div className="close_oppor_info_grid">
                    <div className="close_oppor_info_item">
                    <span className="close_oppor_info_label">Purpose</span>
                    <span className="close_oppor_info_value">{opportunityData.purpose}</span>
                    </div>
                    <div className="close_oppor_info_item">
                    <span className="close_oppor_info_label">Location</span>
                    <span className="close_oppor_info_value">{opportunityData.location}</span>
                    </div>
                    <div className="close_oppor_info_item">
                    <span className="close_oppor_info_label">Art Type</span>
                    <span className="close_oppor_info_value">{opportunityData.artType}</span>
                    </div>
                    <div className="close_oppor_info_item">
                    <span className="close_oppor_info_label">Scheduled Date</span>
                    <span className="close_oppor_info_value">
                        {new Date(opportunityData.performanceDate).toLocaleDateString()}
                    </span>
                    </div>
                </div>
                </div>
            )}

            <form onSubmit={submitHandler} className="close_oppor_form">
                {/* Performance Details Section */}
                <div className="close_oppor_section">
                <h2 className="close_oppor_section_title">
                    <AiOutlineFileText className="close_oppor_section_icon" />
                    Performance Completion Details
                </h2>

                <div className="close_oppor_form_grid">
                    <div className="close_oppor_form_field">
                    <label className="close_oppor_form_label">
                        Event Completion Date*
                    </label>
                    <input
                        required
                        type="date"
                        name="completionDate"
                        min={new Date(opportunityData.performanceDate).toISOString().split("T")[0]}
                        onChange={handlePerformanceDetailsChange}
                        className={`close_oppor_form_input ${
                        validationErrors.completionDate ? "error" : ""
                        }`}
                    />
                    {validationErrors.completionDate && (
                        <span className="close_oppor_error_message">
                        {validationErrors.completionDate}
                        </span>
                    )}
                    </div>
                </div>

                <div className="close_oppor_form_field close_oppor_full_width">
                    <label className="close_oppor_form_label">
                    Performance Summary/Notes*
                    </label>
                    <textarea
                    required
                    name="summary"
                    value={performanceDetails.summary}
                    onChange={handlePerformanceDetailsChange}
                    placeholder="Describe the performance, key highlights, and any notable moments..."
                    className={`close_oppor_form_textarea ${
                        validationErrors.summary ? "error" : ""
                    }`}
                    />
                    {validationErrors.summary && (
                    <span className="close_oppor_error_message">
                        {validationErrors.summary}
                    </span>
                    )}
                </div>

                <div className="close_oppor_form_field close_oppor_full_width">
                    <label className="close_oppor_form_label">
                    Additional Remarks (Optional)
                    </label>
                    <textarea
                    name="remarks"
                    value={performanceDetails.remarks}
                    onChange={handlePerformanceDetailsChange}
                    placeholder="Any additional feedback or remarks..."
                    className="close_oppor_form_textarea"
                    />
                </div>
                </div>

                {/* Image Upload Section */}
                <div className="close_oppor_section">
                <h2 className="close_oppor_section_title">
                    <MdLocationOn className="close_oppor_section_icon" />
                    Performance Proof Images (Geotagged)
                </h2>

                {locationDenied && (
                    <div className="close_oppor_location_alert">
                    <AiOutlineAlert />
                    <span>
                        Location permission denied. Images will be uploaded without geolocation data.
                    </span>
                    </div>
                )}

                <div className="close_oppor_upload_area">
                    <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    style={{ display: "none" }}
                    />
                    <button
                    type="button"
                    className="close_oppor_upload_btn"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    >
                    <AiOutlineCloudUpload />
                    {uploading ? "Processing Images..." : "Click to Upload Images"}
                    </button>
                    <p className="close_oppor_upload_hint">
                    PNG, JPG or JPEG (MAX. 5MB per image)
                    </p>
                </div>

                {validationErrors.images && (
                    <span className="close_oppor_error_message">
                    {validationErrors.images}
                    </span>
                )}

                {uploadedImages.length > 0 && (
                    <div className="close_oppor_images_grid">
                    {uploadedImages.map((img, index) => (
                        <div key={index} className="close_oppor_image_card">
                        <img src={img.preview} alt={`Uploaded ${index + 1}`} />
                        <div className="close_oppor_image_info">
                            <div className="close_oppor_image_meta">
                            {img.latitude && img.longitude ? (
                                <span className="close_oppor_image_location">
                                📍 {img.latitude.toFixed(4)}, {img.longitude.toFixed(4)}
                                </span>
                            ) : (
                                <span className="close_oppor_image_location no-location">
                                📍 No location data
                                </span>
                            )}
                            </div>
                            <button
                            type="button"
                            className="close_oppor_remove_btn"
                            onClick={() => handleRemoveImage(index)}
                            >
                            <MdClose />
                            </button>
                        </div>
                        </div>
                    ))}
                    </div>
                )}

                <p className="close_oppor_upload_count">
                    {uploadedImages.length} image(s) uploaded
                </p>
                </div>

                {/* Artist Reviews Section */}
                <div className="close_oppor_section">
                <h2 className="close_oppor_section_title">
                    <AiOutlineUser className="close_oppor_section_icon" />
                    Artist Reviews & Ratings
                </h2>

                {hiredArtists.length === 0 ? (
                    <p className="close_oppor_no_artists">
                    No artists hired for this opportunity yet.
                    </p>
                ) : (
                    <div className="close_oppor_artists_grid">
                    {hiredArtists && hiredArtists?.map((application) => {
                        // const artist = application.artistId;

                        return (
                        <div key={application._id} className="close_oppor_artist_card">
                            <div className="close_oppor_artist_header">
                             {application.avatar ? (
                                <img
                                src={application.avatar}
                                alt={application.personalInfo.firstName}
                                className="close_oppor_artist_avatar"
                                />
                            ) : (
                              <div className="close_oppor_artist_avatar_placeholder">
                                {application?.personalInfo?.firstName?.charAt(0).toUpperCase()}
                              </div>
                              )}
                            <div className="close_oppor_artist_info">
                                <h4 className="close_oppor_artist_name">{application?.personalInfo?.firstName?.toUpperCase()} {application?.personalInfo?.lastName?.charAt(0).toUpperCase()}</h4>
                                <p className="close_oppor_artist_category">
                                {[...application?.artInfo.artCategory ].join(", ") || "Artist"}
                                </p>
                            </div>
                            </div>

                            <div className="close_oppor_review_section">
                            {/* Rating */}
                            <div className="close_oppor_review_field">
                                <label className="close_oppor_review_label">
                                Rating* (1-5 stars)
                                </label>
                                {/* <div className="close_oppor_rating_input">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                      key={star}
                                      type="button"
                                      className={`close_oppor_star ${
                                          star <= (reviews[artist]?.rating || 0)
                                          ? "active" : ""
                                      }`}
                                      onClick={() =>
                                        handleReviewChange(artist, "rating", star)
                                      }
                                      >
                                      ★
                                    </button>
                                ))}
                                </div> */}

                                <StarReview
                                  value={reviews[application.artistId]?.rating || 0}
                                  onChange={(rating) =>
                                      handleReviewChange(
                                          application.artistId,
                                          "rating",
                                          rating
                                      )
                                  }
                              />
                                {validationErrors[`rating_${application.artistId}`] && (
                                <span className="close_oppor_error_message">
                                    {validationErrors[`rating_${application.artistId}`]}
                                </span>
                                )}
                            </div>

                            {/* Review Comment */}
                            <div className="close_oppor_review_field">
                                <label className="close_oppor_review_label">
                                Review/Feedback (Optional)
                                </label>
                                <textarea
                                value={reviews[application.artistId]?.comment || ""}
                                onChange={(e) =>
                                    handleReviewChange(
                                    application.artistId,
                                    "comment",
                                    e.target.value
                                    )
                                }
                                placeholder="Share your experience with this artist..."
                                className="close_oppor_review_textarea"
                                />
                            </div>
                            </div>
                        </div>
                        );
                    })}
                    </div>
                )} 
                </div>

                {/* Submit Button */}
                <div className="close_oppor_submit_wrapper">
                <button
                    type="submit"
                    disabled={submitting}
                    className="close_oppor_submit_btn"
                >
                    <AiOutlineSave />
                    {submitting ? "Submitting..." : "Submit & Close Opportunity"}
                </button>
                </div>
            </form>
            </div>
        </div>
      </div>
    </>
  );
};

export default CloseUploadedOpportunities;